// Arithmetic only: metric names/keys, parentheses and + - * / %. Never execute user input.
export function compileDynamicFormula(expression: string, fields: {value: string; label: string}[]) {
    const aliases = fields.flatMap(field => [field.label, field.value].map(name => ({
        name: name.replace(/\s/g, ''), key: field.value
    }))).sort((a, b) => b.name.length - a.name.length);
    const source = expression.replace(/\s/g, '');
    let position = 0;
    type Evaluate = (values: Record<string, number>) => number;
    const primary = (): Evaluate => {
        const char = source[position];
        if (char === '+' || char === '-') {
            position++;
            const operand = primary();
            return values => (char === '-' ? -1 : 1) * operand(values);
        }
        if (char === '(') {
            position++;
            const result = sum();
            if (source[position++] !== ')') throw new Error('Missing closing parenthesis.');
            return result;
        }
        const field = aliases.find(alias => alias.name && source.startsWith(alias.name, position)
            && (!source[position + alias.name.length] || /[+\-*/%)]/.test(source[position + alias.name.length])));
        if (field) {
            position += field.name.length;
            return values => Number(values[field.key]) || 0;
        }
        const number = source.slice(position).match(/^(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?/i);
        if (number) {
            position += number[0].length;
            return () => Number(number[0]);
        }
        throw new Error('Use an existing metric name and arithmetic operators (+, -, *, /, %).');
    };
    const product = (): Evaluate => {
        let left = primary();
        while (['*', '/', '%'].includes(source[position])) {
            const operator = source[position++], previous = left, right = primary();
            left = values => operator === '*' ? previous(values) * right(values)
                : operator === '/' ? previous(values) / right(values) : previous(values) % right(values);
        }
        return left;
    };
    const sum = (): Evaluate => {
        let left = product();
        while (['+', '-'].includes(source[position])) {
            const operator = source[position++], previous = left, right = product();
            left = values => operator === '+' ? previous(values) + right(values) : previous(values) - right(values);
        }
        return left;
    };
    const evaluate = sum();
    if (position !== source.length) throw new Error('Invalid formula. Check the metric names and parentheses.');
    return (values: Record<string, number>) => {
        const result = evaluate(values);
        return Number.isFinite(result) ? result : 0;
    };
}
